import React, { useEffect, useRef, useState } from "react";
import {
    Alert,
    Drawer,
    Form,
    Input,
    Select,
    TreeSelect,
    Switch,
    Button,
    Space,
    Divider,
    Row,
    Col,
    message,
} from "antd";
import { assetAPIWithErrorHandling, assetHelpers } from "../../../api/asset/asset";
import { neutral } from "../../../theme/tokens";
import { organizationAPIWithErrorHandling } from "../../../api/organization/organization";

const { TextArea } = Input;

/**
 * Drawer thêm mới / chỉnh sửa tài sản.
 *
 * Form chỉ có 8 trường, không upload, không editor — thao tác ngay trên danh
 * sách giữ được trang và bộ lọc đang bật, đỡ một vòng điều hướng.
 *
 * Dữ liệu điền sẵn lấy thẳng từ dòng trên bảng: API danh sách đã trả model đầy
 * đủ kèm quan hệ organization/customer nên không cần gọi lại getAssetById.
 *
 * @param {boolean} open
 * @param {object|null} record   Có -> chế độ sửa, null -> chế độ thêm mới.
 * @param {object[]} treeData    Cây đơn vị (trang danh sách đã nạp, truyền xuống để không gọi lại API).
 * @param {function} onClose
 * @param {function} onSaved     Gọi sau khi lưu thành công để trang danh sách nạp lại.
 */
export default function AssetFormDrawer({
    open,
    record = null,
    treeData = [],
    onClose,
    onSaved,
}) {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [fetchingCustomers, setFetchingCustomers] = useState(false);
    // Số khoản thu chưa thanh toán sẽ đổi theo khi lưu — cảnh báo đúng phạm vi
    // thay vì một câu chung chung mà ai cũng lướt qua.
    const [affected, setAffected] = useState(null);
    const isEdit = !!record;

    // Chủ sở hữu đang gán của bản ghi đang sửa. Danh sách khách hàng chỉ nạp 100
    // bản ghi đầu của đơn vị, người này có thể nằm ngoài đó nên phải giữ riêng để
    // chèn vào options, nếu không ô chọn sẽ hiện ID trần thay vì tên.
    const ownerRef = useRef(null);

    // Lỗi ở đây không được chặn việc sửa tài sản: thiếu con số thì ẩn cảnh báo,
    // còn hơn dựng một drawer không dùng được chỉ vì một lời nhắc.
    const loadAffected = async (assetId) => {
        setAffected(null);
        try {
            const res = await assetAPIWithErrorHandling.getAffectedFees(assetId);
            setAffected(res?.data?.data ?? res?.data ?? null);
        } catch {
            setAffected(null);
        }
    };

    const loadCustomers = async (orgId, search = "") => {
        const owner = ownerRef.current;

        if (!orgId) {
            setCustomers(owner ? [owner] : []);
            return;
        }

        setFetchingCustomers(true);
        try {
            const response = await organizationAPIWithErrorHandling.getCustomerAllByOrg(orgId, {
                search,
                per_page: 100,
            });
            const data = response.data?.data || response.data || [];
            const list = Array.isArray(data) ? data : [];

            setCustomers(
                owner && !list.some((c) => c.id === owner.id) ? [owner, ...list] : list
            );
        } catch (error) {
            console.error("Error loading customers:", error);
            setCustomers(owner ? [owner] : []);
        } finally {
            setFetchingCustomers(false);
        }
    };

    useEffect(() => {
        if (!open) return;

        if (isEdit) {
            ownerRef.current = record.customer
                ? {
                    id: record.customer.id,
                    name: record.customer.name,
                    phone: record.customer.phone,
                }
                : null;

            // TreeSelect dựng value bằng String(id), còn API trả organization_id kiểu số.
            const orgId = record.organization_id ?? record.organization?.id;

            form.setFieldsValue({
                name: record.name,
                street: record.street,
                description: record.description,
                status: !!record.status,
                organization_id: orgId ? String(orgId) : undefined,
                customer_id: record.customer_id ?? record.customer?.id,
                representative_name: record.representative_name,
                representative_phone: record.representative_phone,
                use_representative_name: !!record.use_representative_name,
            });

            loadCustomers(orgId);
            loadAffected(record.id);
        } else {
            ownerRef.current = null;
            form.resetFields();
            setCustomers([]);
            setAffected(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, record]);

    const onChangeOrg = (newValue) => {
        // Đổi đơn vị thì chủ sở hữu cũ không còn hợp lệ.
        ownerRef.current = null;
        form.setFieldsValue({ customer_id: undefined });
        loadCustomers(newValue);
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const assetData = assetHelpers.formatAssetData(values);

            const validation = assetHelpers.validateAssetData(assetData);
            if (!validation.isValid) {
                Object.keys(validation.errors).forEach((field) => {
                    form.setFields([{ name: field, errors: [validation.errors[field]] }]);
                });
                return;
            }

            if (isEdit) {
                await assetAPIWithErrorHandling.updateAsset(record.id, assetData);
                message.success("Cập nhật tài sản thành công!");
            } else {
                await assetAPIWithErrorHandling.createAsset(assetData);
                message.success("Thêm tài sản thành công!");
            }

            onSaved?.();
            onClose?.();
        } catch (error) {
            // Drawer hẹp, toast dễ bị bỏ qua nên lỗi theo trường phải hiện ngay tại ô.
            if (error.fieldErrors) {
                form.setFields(
                    Object.entries(error.fieldErrors).map(([name, errors]) => ({
                        name,
                        errors: Array.isArray(errors) ? errors : [errors],
                    }))
                );
                message.error("Dữ liệu chưa hợp lệ, kiểm tra lại các ô được đánh dấu.");
            } else {
                message.error(
                    error.message || (isEdit ? "Không thể cập nhật tài sản" : "Không thể thêm tài sản")
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            title={isEdit ? "Chỉnh sửa tài sản" : "Thêm tài sản"}
            width="min(640px, 100vw)"
            open={open}
            onClose={onClose}
            destroyOnHidden
            maskClosable={!submitting}
            footer={
                <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={onClose} disabled={submitting}>
                        Hủy
                    </Button>
                    <Button type="primary" loading={submitting} onClick={() => form.submit()}>
                        {isEdit ? "Lưu thay đổi" : "Thêm mới"}
                    </Button>
                </Space>
            }
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                requiredMark
                initialValues={{ status: true, use_representative_name: false }}
            >
                {isEdit && affected?.fees > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={
                            <>
                                Thay đổi sẽ được áp dụng cho <strong>{affected.fees} khoản thu chưa thanh toán</strong>
                                {affected.collections > 0 && <> thuộc <strong>{affected.collections} đợt thu</strong></>}.
                            </>
                        }
                        description="Ghi đè cả những dòng đã được sửa tay trong đợt thu. Khoản thu đã thanh toán hoặc đã thu một phần giữ nguyên thông tin cũ."
                    />
                )}

                <Row gutter={16}>
                    <Col xs={24} sm={14}>
                        <Form.Item
                            name="name"
                            label="Tên tài sản"
                            rules={[
                                { required: true, message: "Vui lòng nhập tên tài sản!" },
                                { min: 2, message: "Tên tài sản phải có ít nhất 2 ký tự!" },
                                { max: 100, message: "Tên tài sản không được vượt quá 100 ký tự!" },
                            ]}
                        >
                            <Input placeholder="Nhập tên tài sản" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={10}>
                        <Form.Item name="street" label="Đường">
                            <Input placeholder="Tên đường" />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="description" label="Chi tiết">
                    <TextArea
                        placeholder="Chi tiết tài sản/phòng..."
                        rows={3}
                        maxLength={500}
                        showCount
                    />
                </Form.Item>

                <Divider orientation="left" plain style={{ marginTop: 8 }}>
                    Chủ sở hữu
                </Divider>

                <Form.Item
                    name="organization_id"
                    label="Thuộc đơn vị"
                    rules={[{ required: true, message: "Vui lòng chọn đơn vị!" }]}
                >
                    <TreeSelect
                        treeDataSimpleMode
                        style={{ width: "100%" }}
                        styles={{ popup: { root: { maxHeight: 400, overflow: "auto" } } }}
                        placeholder="Chọn đơn vị"
                        onChange={onChangeOrg}
                        treeData={treeData}
                        allowClear
                        showSearch
                        treeNodeFilterProp="title"
                    />
                </Form.Item>

                <Form.Item
                    name="customer_id"
                    label="Khách hàng sở hữu"
                    rules={[{ required: true, message: "Vui lòng chọn khách hàng sở hữu!" }]}
                >
                    <Select
                        showSearch
                        placeholder="Tìm và chọn khách hàng"
                        filterOption={false}
                        onSearch={(val) => loadCustomers(form.getFieldValue("organization_id"), val)}
                        loading={fetchingCustomers}
                        notFoundContent={fetchingCustomers ? "Đang tải..." : "Không có khách hàng"}
                        allowClear
                        options={customers.map((c) => ({
                            value: c.id,
                            label: c.phone ? c.name + " (" + c.phone + ")" : c.name,
                        }))}
                    />
                </Form.Item>

                <Divider orientation="left" plain style={{ marginTop: 8 }}>
                    Chủ hộ
                </Divider>
                <div style={{ marginTop: -8, marginBottom: 16, fontSize: 12, color: neutral[500] }}>
                    Không nhập thì tự động lấy từ khách hàng sở hữu.
                </div>

                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="representative_name" label="Tên chủ hộ">
                            <Input placeholder="Nhập tên chủ hộ" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="representative_phone" label="Số điện thoại chủ hộ">
                            <Input placeholder="Nhập số điện thoại chủ hộ" />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Chỉ đọc lúc tạo khoản thu — đổi ở đây không sửa khoản thu đã có */}
                <Form.Item
                    name="use_representative_name"
                    label="Tên trên khoản thu và tin nhắn"
                    valuePropName="checked"
                    extra="Bật: khoản thu mới ghi tên chủ hộ ở trên thay cho tên khách hàng. Khoản thu đã tạo giữ nguyên tên cũ."
                >
                    <Switch checkedChildren="Tên chủ hộ" unCheckedChildren="Tên khách hàng" />
                </Form.Item>

                {/* Công tắc bật/tắt để cuối, sau khi đã nhập xong dữ liệu */}
                <Form.Item name="status" label="Trạng thái" valuePropName="checked">
                    <Switch checkedChildren="Hoạt động" unCheckedChildren="Không hoạt động" />
                </Form.Item>
            </Form>
        </Drawer>
    );
}
